// @ts-ignore
import config from '../../temp/config';
import {
    escapeCoveoQueryValue,
    getCoveoLocationFilter,
    getCoveoTopicName,
    toIsoDateString,
} from '../shared/coveoEventFieldMapping';
import { ItemProps } from './definitions';

export const fetchCoveoEvents = async (
    topicDisplayName: string | undefined,
    subtopicDisplayName: string | undefined,
    countryName: string | undefined,
    options?: { numberOfResults?: number; sortCriteria?: string; cpdEventsOnly?: boolean }
): Promise<Omit<ItemProps, 'handleCardAnalytics'>[]> => {
    try {
        
        const isLocalHost = typeof window !== 'undefined' &&
            (window.location.hostname === 'localhost' || window.location.hostname.includes('cpa.localhost'));
        const isNonProd = typeof window !== 'undefined' && (window.location.hostname.includes('uat') || window.location.hostname.includes('preprod'));

        // Use the local webpack proxy to bypass CORS in dev, UAT API for UAT, otherwise absolute prod URL
        const endpoint = isLocalHost
            ? '/coveo-proxy/coveo/search'
            : isNonProd
                ? 'https://pp.api.cpaaustralia.com.au/coveo/search'
                : 'https://api.cpaaustralia.com.au/coveo/search';

        // Default to generic CPA events if no specific topic name is available
        let aqQuery = '@source=="CPA Events"';

        // Add dynamic filtering based on the clicked tab's displayName if needed
        if (topicDisplayName) {
            aqQuery += ` AND @commontopics=="${escapeCoveoQueryValue(getCoveoTopicName(topicDisplayName))}"`;
        }
        if (subtopicDisplayName) {
            aqQuery += ` AND @cventfederatedsearchtopics=="${escapeCoveoQueryValue(subtopicDisplayName)}"`;
        }
        // Apply country filtering using an optimal metadata label
        if (countryName) {
            aqQuery += ` AND ${getCoveoLocationFilter(countryName)}`;
        }
        // Professional Development (CPD Uplift) surfaces only CVENT events that attract CPD hours
        // and have been ticked for display on the catalogue (ie exclude networking/invitation-only events)
        if (options?.cpdEventsOnly) {
            aqQuery += ` AND @hascpdhours=="Yes" AND @displayeventoncatalogue=="Yes"`;
        }

        const requestBody = {
            aq: aqQuery,
            numberOfResults: options?.numberOfResults ?? 9,
            sortCriteria: options?.sortCriteria ?? "@cventstartdateutc ascending",
            fieldsToInclude: [
                "title",
                "uri",
                "cventpricing",
                "cventstartdate_display",
                "cventformattedpricing",
                "commontopics",
                "cventfederatedsearchtopics",
                "commonprice",
                "cventdeliverymode",
                "commonmobiletopicgroup",
                "commoncontenttype",
                "commonmobilelocationstate",
                "cventcpdhours",
                "commonmobilearticleimageurl",
                "cventenddateutc",
                "cventstartdateutc"
            ]
        };

        const response = await fetch(endpoint, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(requestBody)
        });

        if (!response.ok) {
            console.warn(`Coveo API returned ${response.status}`);
            return [];
        }

        const { results } = await response.json();

        if (!results || !Array.isArray(results)) {
            return [];
        }

        // Map Coveo raw fields strictly into the React ItemProps schema so UI components never break
        const mappedItems: Omit<ItemProps, 'handleCardAnalytics'>[] = results.map((result: any) => {
            const raw = result.raw || {};
            const commonPrice = result?.raw?.commonprice;
            const pricing = Array.isArray(commonPrice)
                ? commonPrice[0]
                : commonPrice;
            return {
                id: result.uniqueId || raw.uri || Math.random().toString(36).substring(7),
                type: 'event', // hardcode to distinct from courses
                title: result.title || raw.title || 'Event',
                description: raw.cventstartdate_display ? `Starts: ${raw.cventstartdate_display}` : '',
                startDate: raw.cventstartdate_display ? raw.cventstartdate_display : '',
                schemaStartDate: toIsoDateString(raw.cventstartdateutc),
                link: result.clickUri || raw.uri || '#',
                category: raw.cventdeliverymode || 'Event',
                cpdHours: raw.cventcpdhours ? raw.cventcpdhours.toString() : null,
                headingLevel: 3,
                rating: null, // events are not rated; only courses carry a rating
                eventPriceType: pricing,
                listingImage: raw.commonmobilearticleimageurl ? {
                    value: {
                        src: raw.commonmobilearticleimageurl,
                        alt: result.title || 'Event Image',
                        height: 158,
                        width: 304
                    }
                } : null,
                tags: [
                    { id: 'event-tag', displayName: 'Event' },
                    ...(raw.commontopics ? [{ id: 'topic-tag', displayName: raw.commontopics }] : []),
                ],
            };
        });

        return mappedItems;

    } catch (error) {
        console.error("Error dynamically fetching Coveo Events:", error);
        return [];
    }
};
